import React, { lazy } from 'react';
import { LocationProvider } from '@reach/router';
import { PrismicPreviewProvider } from 'gatsby-plugin-prismic-previews';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Provider } from '@components/Context';
import { NavProvider } from '@components/navigation/Context';
import { linkResolver } from './linkResolver';
import { RECAPTCHA_ID } from './src/constants';
import { ensureRecaptchaContainer } from './src/utils/ensureRecaptchaContainer';
import { extractLanguageFromUrl, getCountryCodeByLocale } from './src/utils/language';

export default function Wrapper({ element, pathname, helmetContext }) {
  // Instantiating store in `wrapRootElement` handler ensures:
  //  - there is fresh store for each SSR page
  //  - it will be called only once in browser, when React mounts
  const currentPathname = typeof window !== 'undefined' ? window?.location?.pathname : pathname;
  const country = getCountryCodeByLocale(extractLanguageFromUrl(currentPathname)?.locale);

  // Get reCAPTCHA site key from environment
  const recaptchaSiteKey = process.env.RECAPTCHA_SITE_KEY;

  // Global CSS for reCAPTCHA - badge hidden per Google's terms compliance
  const recaptchaStyles = `
    /* Hide reCAPTCHA badge (requires disclosure text in privacy policy) */
    .grecaptcha-badge {
      display: none !important;
      visibility: hidden !important;
    }
    
    /* General styling for all reCAPTCHA containers */
    [id^="recaptcha-container"] {
      z-index: 1000 !important;
      visibility: hidden !important;
    }
    
    /* Show reCAPTCHA containers only when they have content */
    [id^="recaptcha-container"]:not(:empty) {
      visibility: visible !important;
    }
    
    /* Default global container positioning (for login scenarios) */
    #recaptcha-container {
      position: fixed !important;
      bottom: 20px !important;
      left: 20px !important;
    }

    #recaptcha-container-login {
      margin-bottom: 20px;
      min-width: 256px !important;
      min-height: 60px !important;
    }
    #recaptcha-container-login > div {
      display: flex;
      align-items: flex-end;
      flex-direction: column;
    }
  `;

  // Create and inject the style element (browser only) - prevent duplicates
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('recaptcha-styles');
    if (!existingStyle) {
      const styleElement = document.createElement('style');
      styleElement.id = 'recaptcha-styles';
      styleElement.type = 'text/css';
      styleElement.innerHTML = recaptchaStyles;
      document.head.appendChild(styleElement);
    }
  }

  ensureRecaptchaContainer();

  const isPrismicPreviewEnabled = process.env.GATSBY_ACTIVE_ENV !== 'prod';

  const prismicRepositoryConfigs = [
    {
      repositoryName: 'ecommerce-website',
      typePrefix: 'ecommerce',
      componentResolver: {
        page: lazy(() => import('@components/page/Page')),
        product: lazy(() => import('@components/products/ProductPage')),
        science_page: lazy(() => import('@components/science/SciencePage'))
      },
      linkResolver
    }
  ];

  const innerContent = (
    <>
      {element}
    </>
  );

  const content = (
    <HelmetProvider context={helmetContext}>
      <LocationProvider>
        <Provider country={country}>
          <NavProvider>
            {isPrismicPreviewEnabled ? (
              <PrismicPreviewProvider repositoryConfigs={prismicRepositoryConfigs}>
                {innerContent}
              </PrismicPreviewProvider>
            ) : (
              innerContent
            )}
          </NavProvider>
        </Provider>
      </LocationProvider>
    </HelmetProvider>
  );

  // Wrap with Google reCAPTCHA v3 provider if site key is available
  if (recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
          nonce: undefined
        }}
        container={{
          element: RECAPTCHA_ID,
          parameters: {
            badge: 'bottomleft'
          }
        }}
      >
        {content}
      </GoogleReCaptchaProvider>
    );
  }

  return content;
}

Wrapper.propTypes = {
  element: PropTypes.element.isRequired,
  pathname: PropTypes.string,
  helmetContext: PropTypes.object
};
